import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import JobCard from '../components/JobCard';
import { StarRating } from './Reviews';
import { API_URL, formatDate, getLicenseBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Building2,
  MapPin,
  Globe,
  Star,
  Briefcase,
  ArrowLeft,
  Loader2,
  Mail,
  Search,
  Users,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import Seo from '../components/Seo';

// Employers List Page
const EmployersList = () => {
  const navigate = useNavigate();
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    fetchEmployers();
  }, []);

  const fetchEmployers = async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API_URL}/employers?${params.toString()}`);
      setEmployers(response.data.employers);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
    } catch (error) {
      toast.error('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEmployers(search);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title="Social Work Employers & Agencies Hiring"
        description="Browse verified social work employers and agencies hiring on I Need A Social Worker. Explore organizations posting LBSW, LMSW and LCSW jobs nationwide."
        path="/employers"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-gray-900 mb-2">
            Employers
          </h1>
          <p className="text-gray-600">
            {pagination.total} companies hiring LMSW and LCSW professionals
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-8">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search employers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <Button type="submit" className="bg-primary hover:bg-primary-hover text-white h-11 px-6">
              Search
            </Button>
          </div>
        </form>

        {/* Employers Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : employers.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {employers.map((employer) => (
              <Link
                key={employer.user_id}
                to={`/employers/${employer.user_id}`}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:border-secondary/50 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {employer.company_name || employer.name}
                    </h3>
                    {employer.employer_badge && (
                      <Badge className="bg-secondary/10 text-secondary text-xs mt-1">
                        {employer.employer_badge}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Rating */}
                {employer.avg_rating ? (
                  <div className="flex items-center gap-2 mb-3">
                    <StarRating rating={employer.avg_rating} size="sm" />
                    <span className="text-sm font-medium">{employer.avg_rating}</span>
                    <span className="text-sm text-gray-500">({employer.review_count} reviews)</span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 mb-3">No reviews yet</p>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {employer.active_job_count || 0} active jobs
                  </span>
                </div>

                {employer.company_description && (
                  <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                    {employer.company_description}
                  </p>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 mb-2">No employers found</h3>
            <p className="text-gray-500">Try a different search term</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Employer Profile Page
const EmployerProfile = () => {
  const { employerId } = useParams();
  const navigate = useNavigate();
  const { user, isJobSeeker } = useAuth();
  
  const [employer, setEmployer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [messageData, setMessageData] = useState({ subject: '', content: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchEmployerProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);

  const fetchEmployerProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/employers/${employerId}`);
      setEmployer(response.data.employer);
      setJobs(response.data.jobs);
      setReviews(response.data.reviews);
    } catch (error) {
      toast.error('Employer not found');
      navigate('/employers');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    if (!messageData.subject || !messageData.content) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/messages`,
        {
          recipient_id: employerId,
          subject: messageData.subject,
          content: messageData.content,
        },
        { headers: {  } }
      );
      
      toast.success('Message sent!');
      setContactOpen(false);
      setMessageData({ subject: '', content: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!employer) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        exactTitle={employer?.company_name ? `${employer.company_name} — Social Work Jobs & Hiring | I Need A Social Worker` : 'Employer Profile | I Need A Social Worker'}
        description={employer?.company_name ? `See open social work positions and company details for ${employer.company_name} on I Need A Social Worker.` : 'View this social work employer profile and their open positions on I Need A Social Worker.'}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <button
          onClick={() => navigate('/employers')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Employers
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-10 h-10 text-primary" />
            </div>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900">
                  {employer.company_name || employer.name}
                </h1>
                {employer.employer_badge && (
                  <Badge className="bg-secondary text-white">
                    {employer.employer_badge}
                  </Badge>
                )}
              </div>
              
              {/* Rating */}
              {employer.avg_rating ? (
                <div className="flex items-center gap-2 mb-4">
                  <StarRating rating={employer.avg_rating} />
                  <span className="font-semibold">{employer.avg_rating}</span>
                  <Link 
                    to={`/reviews/employer/${employerId}`}
                    className="text-sm text-primary hover:underline"
                  >
                    ({employer.review_count} reviews)
                  </Link>
                </div>
              ) : (
                <p className="text-gray-500 mb-4">No reviews yet</p>
              )}

              {/* Info */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                {employer.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {employer.location}
                  </span>
                )}
                {employer.company_website && (
                  <a 
                    href={employer.company_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {employer.company_description && (
                <p className="text-gray-600">{employer.company_description}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {isJobSeeker && (
                <Link to={`/reviews/employer/${employerId}`}>
                  <Button variant="outline">
                    <Star className="w-4 h-4 mr-2" />
                    Write Review
                  </Button>
                </Link>
              )}
              {user && (
                <Button
                  onClick={() => setContactOpen(true)}
                  className="bg-primary hover:bg-primary-hover text-white"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Contact
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Active Jobs */}
        <div className="mb-8">
          <h2 className="font-serif text-xl font-semibold text-gray-900 mb-4">
            Active Jobs ({jobs.length})
          </h2>
          {jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job) => (
                <JobCard key={job.job_id} job={job} showActions={false} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 text-center py-8">
              <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No active job listings</p>
            </div>
          )}
        </div>

        {/* Recent Reviews */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl font-semibold text-gray-900">
              Recent Reviews
            </h2>
            {reviews.length > 0 && (
              <Link
                to={`/reviews/employer/${employerId}`}
                className="text-sm text-primary hover:underline"
              >
                View all reviews
              </Link>
            )}
          </div>
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.review_id} className="bg-white rounded-xl p-5 border border-gray-100">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{review.reviewer_name}</p>
                      <p className="text-sm text-gray-500">{formatDate(review.created_at)}</p>
                    </div>
                    <StarRating rating={review.rating} size="sm" />
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1">{review.title}</h4>
                  <p className="text-sm text-gray-600">{review.review_text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 text-center py-8">
              <Star className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No reviews yet</p>
              {isJobSeeker && (
                <Link to={`/reviews/employer/${employerId}`}>
                  <Button variant="link" className="text-primary mt-2">
                    Be the first to review
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Contact {employer.company_name || employer.name}
            </DialogTitle>
            <DialogDescription>
              Send a message to this employer
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendMessage} className="space-y-4 py-4">
            <div>
              <Input
                value={messageData.subject}
                onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                placeholder="Subject"
              />
            </div>
            <div>
              <Textarea
                value={messageData.content}
                onChange={(e) => setMessageData({ ...messageData, content: e.target.value })}
                placeholder="Your message..."
                rows={5}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending}
                className="bg-primary hover:bg-primary-hover text-white"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { EmployersList, EmployerProfile };
